import { createReducer, on } from '@ngrx/store';
import { Season, SeasonChallengesResponse, SeasonUnclaimedRewards, SeasonLevelsResponse, ActiveSeasonsResponse, SeasonProgressResponse, SeasonLevelProgress, SeasonsResponse } from '../../models/season.model';
import { SeasonActions } from '../actions/season.actions';

export interface SeasonState {
  allSeasons: SeasonsResponse | null;
  activeSeasons: ActiveSeasonsResponse | null;
  season: Season | null;
  progress: SeasonProgressResponse | null;
  challenges: SeasonChallengesResponse | null;
  levels: SeasonLevelsResponse | null;
  levelsProgress: SeasonLevelProgress[] | null;
  lastFromNumber: number;
  lastToNumber: number;
  unclaimedRewards: SeasonUnclaimedRewards | null;
  loading: boolean;
  error: any;
}

export const initialState: SeasonState = {
  allSeasons: null,
  activeSeasons: null,
  season: null,
  progress: null,
  challenges: null,
  levels: null,
  levelsProgress: null,
  lastFromNumber: 1,
  lastToNumber: 5,
  unclaimedRewards: null,
  loading: false,
  error: null
};

export const seasonReducer = createReducer(
  initialState,
  on(SeasonActions.loadSeasons, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadSeasonsSuccess, (state, { response }) => ({ ...state, allSeasons: response, loading: false })),
  on(SeasonActions.loadSeasonsFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadActiveSeasons, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadActiveSeasonsSuccess, (state, { response }) => ({ ...state, activeSeasons: response, loading: false })),
  on(SeasonActions.loadActiveSeasonsFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadSeasonInfo, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadSeasonInfoSuccess, (state, { season }) => ({ ...state, season, loading: false })),
  on(SeasonActions.loadSeasonInfoFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadSeasonProgress, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadSeasonProgressSuccess, (state, { response }) => ({
    ...state,
    progress: response,
    season: {
      ...state.season,
      ...response.seasonInfo,
      ...response
    },
    loading: false
  })),
  on(SeasonActions.loadSeasonProgressFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadSeasonChallenges, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadSeasonChallengesSuccess, (state, { response }) => ({ ...state, challenges: response, loading: false })),
  on(SeasonActions.loadSeasonChallengesFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadSeasonLevels, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadSeasonLevelsSuccess, (state, { response }) => ({ ...state, levels: response, loading: false })),
  on(SeasonActions.loadSeasonLevelsFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadSeasonLevelsProgress, (state, { fromNumber, toNumber }) => ({
    ...state,
    loading: true,
    lastFromNumber: fromNumber,
    lastToNumber: toNumber
  })),
  on(SeasonActions.loadSeasonLevelsProgressSuccess, (state, { response }) => ({ ...state, levelsProgress: response, loading: false })),
  on(SeasonActions.loadSeasonLevelsProgressFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadUnclaimedRewards, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadUnclaimedRewardsSuccess, (state, { response }) => ({ ...state, unclaimedRewards: response, loading: false })),
  on(SeasonActions.loadUnclaimedRewardsFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.claimChallengeSuccess, (state, { challengeId }) => {
    if (!state.challenges) return state;
    const updatedChallenges = state.challenges.challenges.map(ch =>
      ch.rewardId === challengeId
        ? {
            ...ch,
            challengeProgress: ch.challengeProgress
              ? { ...ch.challengeProgress, isRewardClaimed: true }
              : ch.challengeProgress
          }
        : ch
    );
    return {
      ...state,
      challenges: { ...state.challenges, challenges: updatedChallenges }
    };
  }),

  on(SeasonActions.claimSeasonRewardSuccess, (state, { rewardIds }) => {
    const updatedLevelsProgress = state.levelsProgress?.map(levelProgress => ({
      ...levelProgress,
      rewardProgresses: levelProgress.rewardProgresses.map(rewardProgress =>
        rewardIds.includes(rewardProgress.rewardId ?? rewardProgress.rewardDetailsInfo.id)
          ? { ...rewardProgress, isRewardClaimed: true }
          : rewardProgress
      )
    })) ?? state.levelsProgress;

    return {
      ...state,
      levelsProgress: updatedLevelsProgress
    };
  }),

  on(SeasonActions.claimAllUnclaimedRewardsSuccess, (state) => ({
    ...state,
    unclaimedRewards: { count: 0 }
  })),
);
